import { useEffect, useState, type CSSProperties } from 'react'

import { ImageFallback } from '@/components/ImageFallback'

type ImageWithFallbackProps = {
  url: string
  alt: string
  height?: number | string
  width?: number | string
  /** Fills the parent's height instead of drawing a fixed one. */
  fillHeight?: boolean
  /** Just the glyph, for a slot too small to hold the message. */
  compact?: boolean
  /**
   * How the slot sits in its surroundings — where it is placed, what corner it
   * has, what ring is around it.
   *
   * Not how big it is and not how the picture fills it: `width`, `height` and
   * the cover fit are written as an inline style, which beats a class, so a
   * `w-*`, `h-*`, `aspect-*` or `object-*` handed in here is dropped without a
   * word. Those are the props above.
   */
  className?: string
}

/** An image that says so when it cannot be loaded, rather than going blank. */
export function ImageWithFallback({
  url,
  alt,
  height = 280,
  width = '100%',
  fillHeight = false,
  compact = false,
  className,
}: ImageWithFallbackProps) {
  const [hasError, setHasError] = useState(false)

  useEffect(() => setHasError(false), [url])

  const box: CSSProperties = fillHeight
    ? { width, height: '100%' }
    : { width, height }

  // The class goes on this branch as well as on the picture: a slot placed by
  // its caller has to stay where it was put when the picture does not load.
  if (hasError || !url) {
    return (
      <div style={box} className={className}>
        <ImageFallback layout={compact ? 'tile' : 'panel'} />
      </div>
    )
  }

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      style={{ ...box, objectFit: 'cover' }}
      className={className}
      onError={() => setHasError(true)}
    />
  )
}
